import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useContractorAuth } from "../context/ContractorAuthContext";
import { ContractorShell } from "../components/ContractorShell";
import { SchemaForm } from "../components/SchemaForm";
import { SignaturePad } from "../components/SignaturePad";
import { SCHEMAS } from "../lib/permitSchemas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, CheckCircle2, PlayCircle, ArrowLeft } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

const SI = SCHEMAS.site_induction;
const PERSONAL = { ...SI, sections: SI.sections.filter((s) => s.id === "personal") };
const ACCEPT = { ...SI, sections: SI.sections.filter((s) => s.id === "acceptance") };
const SITE_VIDEO_URL = "";

export default function SelfInduct() {
  const { site: siteParam } = useParams();
  const navigate = useNavigate();
  const { data, refresh } = useContractorAuth();
  const profile = data?.profile || {};
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(siteParam ? decodeURIComponent(siteParam) : "");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [induction, setInduction] = useState({});
  const [signature, setSignature] = useState(null);
  const [videoConfirmed, setVideoConfirmed] = useState(false);

  useEffect(() => {
    if (!siteParam) api.get("/contractor/sites").then((r) => setSites(r.data.sites)).catch(() => {});
  }, [siteParam]);

  useEffect(() => {
    setInduction((d) => ({
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      email: profile.email || "",
      mobile: profile.mobile || "",
      dbs_number: profile.dbs_number || "",
      emergency_contact: profile.emergency_contact || "",
      ...d,
    }));
  }, [profile.email]);

  const submit = async () => {
    if (!site) return toast.error("Please select your site.");
    if (!induction.emergency_contact || !induction.emergency_number) return toast.error("Please add an emergency contact name and number.");
    if (!induction.accept) return toast.error("Please tick the acceptance declaration to confirm your induction.");
    if (!videoConfirmed) return toast.error("Please confirm you have watched the site induction video.");
    setSaving(true);
    try {
      await api.post("/contractor/induction-submissions", {
        site, induction_data: induction, inductee_signature: signature, video_confirmed: videoConfirmed,
      });
      await refresh();
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <ContractorShell>
        <div className="mx-auto max-w-md rounded-2xl border border-border bg-white p-8 text-center shadow-sm" data-testid="self-induct-success">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-7 w-7 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">Induction submitted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Thanks {profile.first_name}. Your induction for <strong>{site}</strong> has been sent to the site team for review.
          </p>
          <Button className="mt-6 bg-brand hover:bg-brand-dark" onClick={() => navigate("/contractor")} data-testid="back-to-portal-btn">
            Back to my portal
          </Button>
        </div>
      </ContractorShell>
    );
  }

  return (
    <ContractorShell>
      <button onClick={() => navigate("/contractor")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to portal
      </button>
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Site Induction</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">GFM Site Induction</h1>
        <p className="text-sm text-muted-foreground">Complete this induction before starting work. The site team will review and approve it.</p>
      </div>

      <div className="space-y-4">
        <Section title="Part 1 — Your details">
          <div className="mb-4 space-y-1.5">
            <Label>Site</Label>
            {siteParam ? (
              <Input value={site} disabled data-testid="si-site-fixed" />
            ) : (
              <Select value={site} onValueChange={setSite}>
                <SelectTrigger data-testid="si-site"><SelectValue placeholder="Select your site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <SchemaForm schema={PERSONAL} data={induction} onChange={(k, v) => setInduction((d) => ({ ...d, [k]: v }))} />
        </Section>

        <Section title="Health & Safety site induction video">
          {SITE_VIDEO_URL ? (
            <video src={SITE_VIDEO_URL} controls className="mb-4 w-full rounded-md border border-border" data-testid="si-video" />
          ) : (
            <div className="mb-4 flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border py-10 text-muted-foreground" data-testid="si-video">
              <PlayCircle className="h-10 w-10 text-brand" />
              <span className="text-sm">Please watch the health &amp; safety site induction video shown here before continuing.</span>
            </div>
          )}
          <label className="flex cursor-pointer items-center gap-2 rounded-md bg-secondary/60 p-3 text-sm font-medium">
            <Checkbox checked={videoConfirmed} onCheckedChange={(v) => setVideoConfirmed(!!v)} data-testid="si-video-confirm" />
            I confirm I have watched the health &amp; safety site induction video
          </label>
        </Section>

        <Section title="Acceptance">
          <SchemaForm schema={ACCEPT} data={induction} onChange={(k, v) => setInduction((d) => ({ ...d, [k]: v }))} />
        </Section>

        <Section title="Your signature">
          <SignaturePad label="Sign to confirm you have completed this induction" testid="si-signature"
            value={signature} onChange={setSignature} />
        </Section>

        <Button className="w-full bg-brand hover:bg-brand-dark" size="lg" onClick={submit} disabled={saving} data-testid="submit-self-induction">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit induction for review
        </Button>
      </div>
    </ContractorShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-border bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-heading text-lg font-bold tracking-tight text-foreground">{title}</h3>
      {children}
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}
